import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { createApi, errorMessage } from "../../lib/api";
import { ErrorState, Skeleton } from "../../ui";
import { loadTodoGroups } from "./api";
import type { HomeSection, HomeUser, TodoItem } from "./types";

const LIMIT = 8;

type Globals = Window &
	typeof globalThis & {
		isSuperAdmin?: (u: unknown) => boolean;
		BU?: {
			nav?: { config?: { homeSectionsFor?: (u: unknown, opts: Record<string, unknown>) => HomeSection[] } };
			shell?: { isDevSite?: () => boolean; isChatEnabled?: () => boolean };
		};
	};

const g = window as unknown as Globals;

function isAdmin(user: unknown): boolean {
	return typeof g.isSuperAdmin === "function" ? Boolean(g.isSuperAdmin(user)) : false;
}

const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
	paper: { label: "紙單", cls: "border-amber-500/40 bg-amber-500/15 text-amber-200" },
	platform: { label: "平台", cls: "border-sky-500/40 bg-sky-500/15 text-sky-200" },
	delivery: { label: "交收", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-200" },
};

const TONE_PILL: Record<string, string> = {
	amber: "border-amber-500/40 bg-amber-500/15 text-amber-200",
	rose: "border-rose-500/40 bg-rose-500/15 text-rose-200",
	sky: "border-sky-500/40 bg-sky-500/15 text-sky-200",
	slate: "border-slate-600 bg-slate-800 text-slate-300",
};

function money(n: number | null): string {
	if (n == null || Number.isNaN(Number(n))) return "—";
	const v = Number(n);
	return (v > 0 ? "+" : "") + v.toLocaleString("zh-TW", { maximumFractionDigits: 2 });
}

function moneyCls(n: number | null): string {
	const v = Number(n);
	if (!Number.isFinite(v) || v === 0) return "text-slate-300";
	return v > 0 ? "text-emerald-300" : "text-rose-300";
}

function Row({ item }: { item: TodoItem }) {
	const badge = SOURCE_BADGE[item.source] || SOURCE_BADGE.delivery;
	return (
		<a href={item.href} className="bu-card flex items-start justify-between gap-3 p-3.5 transition-colors hover:border-emerald-500/40">
			<div className="min-w-0 space-y-1.5">
				<div className="flex flex-wrap items-center gap-1.5">
					<span className={`rounded border px-1.5 py-0.5 text-[10px] ${badge.cls}`}>{badge.label}</span>
					<span className={`rounded border px-1.5 py-0.5 text-[10px] ${TONE_PILL[item.statusTone] || TONE_PILL.slate}`}>{item.statusLabel}</span>
					<span className="truncate text-sm font-medium text-white">{item.title}</span>
				</div>
				<div className="truncate text-xs text-slate-400">{item.subtitle}</div>
			</div>
			<div className="shrink-0 space-y-1 text-right">
				{item.amount == null ? null : <div className={`font-mono text-sm font-semibold ${moneyCls(item.amount)}`}>{money(item.amount)}</div>}
				<div className="text-[11px] text-emerald-300/90">{item.cta}</div>
			</div>
		</a>
	);
}

function SectionBlock({ title, items, emptyHint }: { title: string; items: TodoItem[]; emptyHint: string }) {
	return (
		<section>
			<h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
				{title}
				{items.length ? <span className="ml-1 normal-case tracking-normal text-slate-500">({items.length})</span> : null}
			</h2>
			{items.length ? (
				<div className="space-y-2">
					{items.slice(0, LIMIT).map((it) => (
						<Row key={`${it.source}-${it.id}`} item={it} />
					))}
				</div>
			) : (
				<div className="rounded-xl border border-dashed border-slate-700 px-4 py-6 text-center text-sm text-slate-500">{emptyHint}</div>
			)}
			{items.length > LIMIT ? <p className="mt-2 text-xs text-slate-500">另有 {items.length - LIMIT} 筆，請至對應專區查看</p> : null}
		</section>
	);
}

function QuickLinks({ sections }: { sections: HomeSection[] }) {
	if (!sections.length) return null;
	return (
		<>
			{sections.map((block) => (
				<section key={block.section}>
					<h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{block.section}</h2>
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{block.cards.map((c) => (
							<a
								key={`${c.href}-${c.title}`}
								href={c.href}
								className={`bu-card block bg-gradient-to-br p-4 ${c.tone || "from-slate-500/15 to-slate-900"}`}
							>
								<div className="flex items-center gap-2 text-sm font-semibold text-white">
									<span aria-hidden="true">{c.icon || ""}</span>
									{c.title}
								</div>
								<div className="mt-1 text-xs text-slate-400">{c.desc || ""}</div>
							</a>
						))}
					</div>
				</section>
			))}
		</>
	);
}

export function AppHomeApp({ token, user }: { token: string; user: HomeUser }) {
	const api = useMemo(() => createApi({ token }), [token]);
	const admin = isAdmin(user);
	const groupsQuery = useQuery({
		queryKey: ["app-home", "todos", admin],
		queryFn: () => loadTodoGroups(api, user, { isAdmin: admin }),
	});

	const groups = groupsQuery.data ?? { confirm: [], today: [], stuck: [] };
	const sections = useMemo(() => {
		const fn = g.BU?.nav?.config?.homeSectionsFor;
		if (typeof fn !== "function") return [] as HomeSection[];
		try {
			return fn(user, {
				isDev: typeof g.BU?.shell?.isDevSite === "function" ? g.BU.shell.isDevSite() : false,
				chatEnabled: typeof g.BU?.shell?.isChatEnabled === "function" ? g.BU.shell.isChatEnabled() : false,
				modules: Array.isArray(user?.modules) ? user.modules : undefined,
			}).filter(Boolean);
		} catch {
			return [] as HomeSection[];
		}
	}, [user]);

	const total = groups.confirm.length + groups.today.length + groups.stuck.length;
	useEffect(() => {
		const sub = document.getElementById("pageSubtitle");
		if (sub) sub.textContent = admin ? "系統管理" : total ? `待處理 ${total} 項` : "目前沒有待辦";
	}, [admin, total]);

	if (groupsQuery.isLoading) {
		return (
			<div className="space-y-6">
				{[0, 1, 2].map((i) => (
					<div key={i} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
						<Skeleton lines={3} />
					</div>
				))}
			</div>
		);
	}
	if (groupsQuery.isError) {
		return <ErrorState message={errorMessage(groupsQuery.error, "載入失敗")} onRetry={() => void groupsQuery.refetch()} />;
	}

	return (
		<div className="space-y-8">
			{admin ? (
				<div className="rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
					管理員工作台以帳號與救火為主。業務待辦請代登代理帳號後檢視。
				</div>
			) : (
				<>
					<SectionBlock title="待我確認" items={groups.confirm} emptyHint="目前沒有待你確認的分帳或平台交收" />
					<SectionBlock title="今日待辦" items={groups.today} emptyHint="沒有待關帳或待出具的紙單" />
					<SectionBlock title="我發起但卡住" items={groups.stuck} emptyHint="沒有駁回、待對方確認或待作廢的項目" />
				</>
			)}
			<QuickLinks sections={sections} />
		</div>
	);
}
